import { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { alpha, useTheme } from '@mui/material/styles';
import TableRowsOutlinedIcon from '@mui/icons-material/TableRowsOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import VerticalSplitOutlinedIcon from '@mui/icons-material/VerticalSplitOutlined';
import { useTranslation } from 'react-i18next';
import {
  ResponsiveContainer, LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, LabelList,
} from 'recharts';
import { InfoTooltip } from './chatMessageStyles';

/**
 * An interactive chart the data agent drew (`create_chart` tool, backend
 * src/data_agent/chart_tool.py). Renders the chart SPEC — type, categories,
 * series, unit — with Recharts; the same spec later feeds the Graphs tab and
 * the PowerPoint/Word renderers (docs/charts/PLAN.md).
 *
 *   spec   {type, title, subtitle, categories, series:[{name, values}],
 *           unit: 'currency'|'percent'|'number', currency, as_of, language}
 *   extra  optional node in the header's action slot (the Graphs star)
 *   view   'chart' | 'table' | 'both' — what the card shows. Controlled when
 *          given (with onViewChange); otherwise the card keeps its own,
 *          starting at defaultView. The star saves it with the graph, and
 *          the Graphs deck builds the slide the same way (feedback
 *          2026-10-12: a graph starred as table came back as a chart).
 *
 * Colours: a fixed categorical order per light/dark mode, validated for
 * colour-blind separation (dataviz validate_palette.js, 2026-10-06). Dark is
 * led by the EarlyLion cyan stepped into the series lightness band; light is
 * the reference order. Three light slots sit under 3:1 contrast, which is why
 * the card always offers the table view.
 */

export const SERIES_COLORS = {
  dark: ['#12a0bf', '#d95926', '#199e70', '#c98500', '#d55181', '#008300', '#9085e9', '#e66767'],
  light: ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'],
};

// Number style of the agents' own answers (backend src/efd_survista_agent/
// fmt.py): apostrophe for thousands, decimal comma except in English, the
// unit word after the figure, the currency last — so the chart reads exactly
// like the text answer it was drawn from.
const SCALE_WORDS = {            // billion, million, thousand
  de: ['Mrd.', 'Mio.', 'Tsd.'],
  en: ['bn', 'm', 'k'],
  fr: ['Md', 'M', 'k'],
  es: ['mil M', 'M', 'mil'],
  it: ['mld', 'mln', 'mila'],
};

const CHART_HEIGHT = 260;
const BAR_SIZE = 24;

/** "1'234'567,8" (de) / "1'234'567.8" (en): `digits` decimals, trailing zeros
 * dropped unless `fixed` (percentages always show two, like the answers). */
function swissNumber(value, lang, digits, fixed = false) {
  const rounded = Math.abs(value).toFixed(digits);
  let [int, frac = ''] = rounded.split('.');
  if (!fixed) frac = frac.replace(/0+$/, '');
  int = int.replace(/\B(?=(\d{3})+(?!\d))/g, "'");
  const sign = value < 0 && Number(rounded) !== 0 ? '-' : '';
  return sign + int + (frac ? (lang === 'en' ? '.' : ',') + frac : '');
}

/** Formatting for one spec: full values (tooltip, table) and compact ticks. */
export function chartFormatter(spec) {
  const lang = SCALE_WORDS[spec.language] ? spec.language : 'de';
  const [big, mid, small] = SCALE_WORDS[lang];
  const isPct = spec.unit === 'percent';
  const currency = spec.unit === 'currency' && spec.currency ? spec.currency : '';
  const all = (spec.series || []).flatMap(s => s.values).filter(v => v != null);
  const maxAbs = all.length ? Math.max(...all.map(v => Math.abs(v))) : 0;
  const decimals = isPct ? 2 : (maxAbs >= 1000 ? 0 : 2);
  const withUnit = (text) => (isPct ? `${text} %` : currency ? `${text} ${currency}` : text);

  const full = (v) => {
    if (v == null || Number.isNaN(v)) return '–';
    return withUnit(swissNumber(v, lang, decimals, isPct));
  };
  const compact = (v) => {
    if (v == null || Number.isNaN(v)) return '';
    if (isPct) return `${swissNumber(v, lang, 1)} %`;
    const abs = Math.abs(v);
    const glue = lang === 'en' ? '' : ' ';
    if (abs >= 1e9) return `${swissNumber(v / 1e9, lang, 2)}${glue}${big}`;
    if (abs >= 1e6) return `${swissNumber(v / 1e6, lang, 1)}${glue}${mid}`;
    if (abs >= 1e4) return `${swissNumber(v / 1e3, lang, 0)}${glue}${small}`;
    return swissNumber(v, lang, abs < 10 ? 2 : 0);
  };
  const date = (iso) => {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
    if (!m) return iso || '';
    return lang === 'en' ? `${m[3]}/${m[2]}/${m[1]}` : `${m[3]}.${m[2]}.${m[1]}`;
  };
  // Value labels on the marks: a percentage keeps the two decimals of the
  // answers ("1,06 %"); amounts stay compact ("4,2 Mio.").
  const label = (v) => (isPct ? full(v) : compact(v));
  // A slice's share of the whole: "42,8 %".
  const share = (v, total) => `${swissNumber(total ? (v / total) * 100 : 0, lang, 1, true)} %`;
  return { full, compact, label, share, date };
}

/** Category-major rows for Recharts: [{category, s0, s1, …}]. */
function toRows(spec) {
  return (spec.categories || []).map((category, i) => {
    const row = { category };
    (spec.series || []).forEach((s, j) => { row[`s${j}`] = s.values[i] ?? null; });
    return row;
  });
}

/** Waterfall rows: invisible base + visible delta; first/last are totals. */
function toWaterfallRows(spec) {
  const values = spec.series?.[0]?.values || [];
  let running = 0;
  return (spec.categories || []).map((category, i) => {
    const v = values[i] ?? 0;
    const total = i === 0 || i === values.length - 1;
    if (total) {
      running = v;
      return { category, base: Math.min(0, v), delta: Math.abs(v), value: v, kind: 'total', top: v };
    }
    const from = running;
    running += v;
    return {
      category, base: Math.min(from, running), delta: Math.abs(v), value: v,
      kind: v >= 0 ? 'up' : 'down', top: Math.max(from, running),
    };
  });
}

/** A rounded axis start below `low` — a bridge whose steps are small against
 *  the totals is unreadable from zero, so its axis starts near the lowest level. */
function niceFloor(low) {
  if (low <= 0) return 0;
  const step = 10 ** Math.floor(Math.log10(low));
  return Math.floor((low * 0.9) / step) * step;
}

/** A round step (1/2/2.5/5 × 10^n) splitting `span` into about `count` parts. */
function niceStep(span, count = 4) {
  const raw = span / count;
  const power = 10 ** Math.floor(Math.log10(raw));
  const unit = raw / power;
  return (unit <= 1 ? 1 : unit <= 2 ? 2 : unit <= 2.5 ? 2.5 : unit <= 5 ? 5 : 10) * power;
}

/** {domain, ticks} rounded outward to round steps, for an axis that follows
 *  the data. The ticks are given explicitly — left to itself, Recharts splits
 *  the domain into equal fifths again (12 / 12,6 / 13,3 …). */
function niceAxis(values, { includeZero = false, labelsBelow = false } = {}) {
  const finite = values.filter(v => v != null && Number.isFinite(v));
  if (includeZero) finite.push(0);
  if (!finite.length) return { domain: [0, 'auto'], ticks: undefined };
  let low = Math.min(...finite);
  let high = Math.max(...finite);
  if (low === high) { low -= Math.abs(low) * 0.1 || 1; high += Math.abs(high) * 0.1 || 1; }
  const step = niceStep(high - low);
  let start = Math.floor(low / step) * step;
  const end = Math.ceil(high / step) * step;
  // A negative column carries its label below its end: keep room for it so
  // it does not run into the category labels under the plot.
  if (labelsBelow && low < 0 && low - start < step * 0.35) start -= step;
  const ticks = [];
  for (let v = start; v <= end + step / 2; v += step) ticks.push(Number(v.toPrecision(12)));
  // A sliver below zero (net FX forwards of -0,6 Mio. next to 113 Mio.) must
  // not open a whole empty step: end the axis just past it, without a tick.
  if (!labelsBelow && low < 0 && start < low - step * 0.75) {
    const tight = low - (high - low) * 0.02;
    return { domain: [tight, end], ticks: ticks.filter(v => v >= tight) };
  }
  return { domain: [start, end], ticks };
}

/**
 * A value label that never wraps (Recharts' own label wraps to the bar width,
 * "12,4 / Mio."). `side` is where it sits: 'top' of a column — below it for a
 * negative one — or 'right' of a horizontal bar.
 */
const valueLabel = (format, color, side = 'top') => (props) => {
  const { x, y, width, height, value, index } = props;
  if (value == null || Number.isNaN(value)) return null;
  const common = { key: index, fill: color, fontSize: 11, fontWeight: 600 };
  // A negative bar may come with a negative height/width: use the extents.
  const top = Math.min(y, y + height);
  const bottom = Math.max(y, y + height);
  const right = Math.max(x, x + width);
  if (side === 'right') {
    return <text {...common} x={right + 6} y={top + Math.abs(height) / 2 + 4} textAnchor="start">{format(value, index)}</text>;
  }
  return (
    <text {...common} x={x + width / 2} y={value < 0 ? bottom + 14 : top - 6} textAnchor="middle">
      {format(value, index)}
    </text>
  );
};

function ChartTooltip({ active, payload, label, fmt, theme, waterfall }) {
  if (!active || !payload || !payload.length) return null;
  const items = waterfall
    ? [{ name: label, color: payload[0]?.payload?.color, value: payload[0]?.payload?.value }]
    : payload.filter(p => p.dataKey !== 'base').map(p => ({
      name: p.name, color: p.color || p.payload?.fill, value: p.value,
    }));
  return (
    <Box sx={{
      bgcolor: theme.palette.mode === 'dark' ? '#10142c' : '#ffffff',
      border: 1, borderColor: 'divider', borderRadius: '8px', px: 1.25, py: 0.75,
      boxShadow: '0 6px 18px rgba(0,0,0,0.25)', fontSize: 12, minWidth: 120,
    }}>
      {!waterfall && label != null && (
        <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'text.primary', mb: 0.25 }}>{label}</Typography>
      )}
      {items.map((it, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, color: 'text.secondary' }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: it.color, flexShrink: 0 }} />
          <Box sx={{ flex: 1 }}>{it.name}</Box>
          <Box sx={{ color: 'text.primary', fontWeight: 600, fontVariantNumeric: 'tabular-nums', ml: 1.5 }}>
            {fmt.full(it.value)}
          </Box>
        </Box>
      ))}
    </Box>
  );
}

function ChartTable({ spec, fmt }) {
  const rows = toRows(spec);
  const cellSx = { px: 1, py: 0.5, borderBottom: 1, borderColor: 'divider', fontSize: 13 };
  return (
    <Box sx={{ overflowX: 'auto', maxHeight: CHART_HEIGHT + 20, overflowY: 'auto' }}>
      <Box component="table" sx={{ borderCollapse: 'collapse', width: '100%' }}>
        <thead>
          <tr>
            <Box component="th" sx={{ ...cellSx, textAlign: 'left', color: 'text.secondary', fontWeight: 600 }} />
            {spec.series.map((s, j) => (
              <Box key={j} component="th" sx={{ ...cellSx, textAlign: 'right', color: 'text.secondary', fontWeight: 600 }}>
                {s.name}
              </Box>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              <Box component="td" sx={{ ...cellSx, color: 'text.primary' }}>{row.category}</Box>
              {spec.series.map((s, j) => (
                <Box key={j} component="td" sx={{ ...cellSx, textAlign: 'right', color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
                  {fmt.full(row[`s${j}`])}
                </Box>
              ))}
            </tr>
          ))}
        </tbody>
      </Box>
    </Box>
  );
}

function ChartBody({ spec, fmt, theme, colors }) {
  const type = spec.type;
  const series = spec.series || [];
  const multi = series.length > 1;
  const surface = theme.palette.background.default;
  const ink = theme.palette.text.secondary;
  const grid = alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.08 : 0.1);
  const axisProps = {
    tick: { fill: ink, fontSize: 11 }, tickLine: false, axisLine: false,
  };
  const tooltip = (
    <Tooltip
      cursor={type === 'line' || type === 'area'
        ? { stroke: alpha(theme.palette.text.primary, 0.25), strokeWidth: 1 }
        : { fill: alpha(theme.palette.text.primary, 0.05) }}
      content={<ChartTooltip fmt={fmt} theme={theme} waterfall={type === 'waterfall'} />}
    />
  );
  const legend = multi && (
    <Legend
      iconType="circle" iconSize={8}
      wrapperStyle={{ fontSize: 12, color: ink, paddingTop: 4 }}
      formatter={(value) => <span style={{ color: ink }}>{value}</span>}
    />
  );
  const anim = { isAnimationActive: true, animationDuration: 900, animationEasing: 'ease-out' };
  // Stable rows: a new array on every render would restart the animation.
  const rows = useMemo(() => toRows(spec), [spec]);
  const hasNegative = series.some(s => s.values.some(v => v != null && v < 0));

  if (type === 'donut') {
    const values = series[0]?.values || [];
    const total = values.reduce((a, v) => a + (v || 0), 0);
    const slices = spec.categories.map((name, i) => ({ name, value: values[i] || 0 }));
    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <PieChart>
          <Pie
            data={slices} dataKey="value" nameKey="name" innerRadius="58%" outerRadius="86%"
            stroke={surface} strokeWidth={2} {...anim}
          >
            {slices.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
          </Pie>
          <Tooltip content={({ active, payload }) => (active && payload?.length ? (
            <ChartTooltip
              active label={null} fmt={{ full: (v) => `${fmt.full(v)} · ${fmt.share(v, total)}` }}
              theme={theme}
              payload={[{ name: payload[0].name, value: payload[0].value, color: payload[0].payload.fill }]}
            />
          ) : null)}
          />
          <Legend
            layout="vertical" align="right" verticalAlign="middle" iconType="circle" iconSize={8}
            wrapperStyle={{ fontSize: 12, maxWidth: '45%' }}
            formatter={(value, entry) => (
              <span style={{ color: ink }}>
                {value} <span style={{ color: theme.palette.text.primary, fontWeight: 600 }}>
                  {total ? fmt.share(entry.payload.value, total) : ''}
                </span>
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'waterfall') {
    const wf = toWaterfallRows(spec);
    // `color`, not `fill`: Recharts would paint a row's `fill` onto every bar
    // of the stack, the invisible base included.
    const colorFor = (kind) => (kind === 'total' ? colors[0]
      : kind === 'up' ? theme.palette.success.main : theme.palette.error.main);
    const data = wf.map(row => ({ ...row, color: colorFor(row.kind) }));
    // The lowest level the bridge reaches: a total's top, a step's base.
    const levels = wf.map(row => (row.kind === 'total' ? row.top : row.base));
    const low = Math.min(...levels);
    const high = Math.max(...wf.map(row => row.top));
    const floor = low > 0 && low > high * 0.4 ? niceFloor(low) : 0;
    const last = wf.length - 1;
    const signed = (v, i) => (i === 0 || i === last || v <= 0 ? fmt.label(v) : `+${fmt.label(v)}`);
    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <BarChart data={data} margin={{ top: 22, right: 8, left: 4, bottom: 0 }}>
          <CartesianGrid vertical={false} stroke={grid} />
          <XAxis dataKey="category" {...axisProps} interval={0} />
          <YAxis {...axisProps} tickFormatter={fmt.compact} width={64}
            domain={[floor, 'auto']} allowDataOverflow={floor > 0} />
          {tooltip}
          <Bar dataKey="base" stackId="w" fill="transparent" isAnimationActive={false} />
          <Bar dataKey="delta" stackId="w" maxBarSize={BAR_SIZE * 2} radius={[4, 4, 0, 0]} {...anim}>
            {data.map((row, i) => <Cell key={i} fill={row.color} />)}
            <LabelList dataKey="value" content={valueLabel(signed, theme.palette.text.secondary)} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    );
  }

  if (type === 'hbar') {
    const height = Math.max(160, rows.length * (multi ? 22 * series.length + 10 : 30) + 40);
    const barAxis = niceAxis(series.flatMap(s => s.values), { includeZero: true });
    const longest = Math.max(...spec.categories.map(c => c.length), 4);
    return (
      <ResponsiveContainer width="100%" height={Math.min(height, 520)}>
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, left: 4, bottom: 0 }}>
          <CartesianGrid horizontal={false} stroke={grid} />
          <XAxis type="number" {...axisProps} tickFormatter={fmt.compact}
            domain={barAxis.domain} ticks={barAxis.ticks} />
          <YAxis type="category" dataKey="category" {...axisProps} width={Math.min(170, 12 + longest * 6.5)} interval={0} />
          {tooltip}
          {legend}
          {hasNegative && <ReferenceLine x={0} stroke={alpha(theme.palette.text.primary, 0.3)} />}
          {series.map((s, j) => (
            <Bar key={j} dataKey={`s${j}`} name={s.name} fill={colors[j % colors.length]}
              maxBarSize={BAR_SIZE} radius={[0, 4, 4, 0]} {...anim}>
              {!multi && (
                <LabelList dataKey={`s${j}`} content={valueLabel(fmt.label, theme.palette.text.secondary, 'right')} />
              )}
            </Bar>
          ))}
        </BarChart>
      </ResponsiveContainer>
    );
  }

  const xAxis = <XAxis dataKey="category" {...axisProps} minTickGap={12} />;
  // Round steps on every value axis. Bars and areas show magnitude, so their
  // axis includes zero; a line shows the course, so its axis follows the data
  // (a flat line at 13 Mio. on a 0–16 scale hides every movement). Stacks
  // are measured by their totals per category.
  const axisValues = type === 'stacked_bar'
    ? rows.flatMap(row => [
      series.reduce((a, _, j) => a + Math.max(row[`s${j}`] || 0, 0), 0),
      series.reduce((a, _, j) => a + Math.min(row[`s${j}`] || 0, 0), 0),
    ])
    : series.flatMap(s => s.values);
  const valueAxis = niceAxis(axisValues, {
    includeZero: type !== 'line',
    labelsBelow: type === 'bar' && !multi,
  });
  const yAxis = (
    <YAxis {...axisProps} tickFormatter={fmt.compact} width={64}
      domain={valueAxis.domain} ticks={valueAxis.ticks} />
  );
  const margin = { top: 12, right: 12, left: 4, bottom: 0 };

  if (type === 'line' || type === 'area') {
    const Chart = type === 'line' ? LineChart : AreaChart;
    const last = rows.length - 1;
    // Only the end point carries a marker (and, for one series, its value).
    // cy, not value: an Area hands its dots value=[base, value].
    const endDot = (color) => (props) => (props.index === last && props.cy != null ? (
      <circle key={props.index} cx={props.cx} cy={props.cy} r={4.5} fill={color} stroke={surface} strokeWidth={2} />
    ) : <g key={props.index} />);
    return (
      <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
        <Chart data={rows} margin={{ ...margin, right: multi ? 12 : 68 }}>
          <CartesianGrid vertical={false} stroke={grid} />
          {xAxis}
          {yAxis}
          {tooltip}
          {legend}
          {hasNegative && <ReferenceLine y={0} stroke={alpha(theme.palette.text.primary, 0.3)} />}
          {series.map((s, j) => {
            const color = colors[j % colors.length];
            const common = {
              type: 'monotone', dataKey: `s${j}`, name: s.name, stroke: color,
              strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round', connectNulls: true,
              dot: endDot(color), activeDot: { r: 5, stroke: surface, strokeWidth: 2 }, ...anim,
            };
            const label = !multi && (
              <LabelList dataKey={`s${j}`} content={(p) => (p.index === last && p.value != null ? (
                <text x={p.x + 8} y={p.y + 4} fill={theme.palette.text.primary} fontSize={11} fontWeight={600}>
                  {fmt.label(p.value)}
                </text>
              ) : null)} />
            );
            return type === 'line'
              ? <Line key={j} {...common}>{label}</Line>
              : <Area key={j} {...common} fill={color} fillOpacity={0.1}>{label}</Area>;
          })}
        </Chart>
      </ResponsiveContainer>
    );
  }

  // bar / stacked_bar
  const stacked = type === 'stacked_bar';
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <BarChart data={rows} margin={{ ...margin, top: stacked || multi ? 12 : 22 }} barGap={2}>
        <CartesianGrid vertical={false} stroke={grid} />
        <XAxis dataKey="category" {...axisProps} interval={rows.length > 12 ? 'preserveStartEnd' : 0} />
        {yAxis}
        {tooltip}
        {legend}
        {hasNegative && <ReferenceLine y={0} stroke={alpha(theme.palette.text.primary, 0.3)} />}
        {series.map((s, j) => (
          <Bar
            key={j} dataKey={`s${j}`} name={s.name} fill={colors[j % colors.length]}
            stackId={stacked ? 'stack' : undefined} maxBarSize={stacked ? BAR_SIZE * 2 : BAR_SIZE}
            radius={!stacked || j === series.length - 1 ? [4, 4, 0, 0] : 0}
            stroke={stacked ? surface : undefined} strokeWidth={stacked ? 1 : 0} {...anim}
          >
            {!multi && !stacked && rows.length <= 12 && (
              <LabelList dataKey={`s${j}`} content={valueLabel(fmt.label, theme.palette.text.secondary)} />
            )}
          </Bar>
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
}

export const VIEWS = ['chart', 'table', 'both'];

/** Grafik | Tabelle | Beides — the same switch in the chat and in Graphs. */
export function ViewSwitch({ value, onChange, size = 'small' }) {
  const { t } = useTranslation();
  const options = [
    { view: 'chart', Icon: InsertChartOutlinedIcon, label: t('graphs_view_chart', { defaultValue: 'Grafik' }) },
    { view: 'table', Icon: TableRowsOutlinedIcon, label: t('graphs_view_table', { defaultValue: 'Tabelle' }) },
    { view: 'both', Icon: VerticalSplitOutlinedIcon, label: t('graphs_view_both', { defaultValue: 'Grafik + Tabelle' }) },
  ];
  return (
    <ToggleButtonGroup
      exclusive size={size} value={value}
      onChange={(e, next) => next && onChange(next)}
      sx={{ '& .MuiToggleButton-root': { px: 0.75, py: 0.25, color: 'text.secondary', borderColor: 'divider' },
        '& .Mui-selected': { color: 'primary.main' } }}
    >
      {options.map(({ view, Icon, label }) => (
        <ToggleButton key={view} value={view} aria-label={label}>
          <InfoTooltip title={label}>
            <Icon sx={{ fontSize: 17 }} />
          </InfoTooltip>
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}

function ChartCard({ spec, extra, view: controlledView, onViewChange, defaultView = 'chart' }) {
  const { t } = useTranslation();
  const theme = useTheme();
  const [ownView, setOwnView] = useState(VIEWS.includes(defaultView) ? defaultView : 'chart');
  const view = VIEWS.includes(controlledView) ? controlledView : ownView;
  const setView = (next) => {
    if (controlledView === undefined) setOwnView(next);
    if (onViewChange) onViewChange(next);
  };
  const fmt = useMemo(() => chartFormatter(spec || {}), [spec]);
  if (!spec || !Array.isArray(spec.series)) return null;
  const colors = SERIES_COLORS[theme.palette.mode === 'light' ? 'light' : 'dark'];
  const asOf = spec.as_of ? t('chart_as_of', { defaultValue: 'Stand: {{date}}', date: fmt.date(spec.as_of) }) : '';
  const meta = [spec.subtitle, asOf].filter(Boolean).join(' · ');

  return (
    <Box
      sx={{
        '@keyframes chartCardRise': {
          from: { opacity: 0, transform: 'translateY(6px)' },
          to: { opacity: 1, transform: 'translateY(0)' },
        },
        width: '100%',
        // SVG text does not pick up the app font on its own.
        fontFamily: theme.typography.fontFamily,
        '& .recharts-wrapper text': { fontFamily: theme.typography.fontFamily },
        border: 1,
        borderColor: alpha(theme.palette.primary.main, 0.35),
        borderRadius: '10px',
        bgcolor: alpha(theme.palette.primary.main, 0.04),
        px: 2, pt: 1.5, pb: 1,
        my: 0.5,
        animation: 'chartCardRise 320ms ease-out both',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 600, color: 'text.primary', fontSize: 15, lineHeight: 1.35 }}>
            {spec.title}
          </Typography>
          {meta && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.25 }}>{meta}</Typography>
          )}
        </Box>
        <ViewSwitch value={view} onChange={setView} />
        {extra}
      </Box>
      {view !== 'table' && (
        <Box role="img" aria-label={spec.title}>
          <ChartBody spec={spec} fmt={fmt} theme={theme} colors={colors} />
        </Box>
      )}
      {view !== 'chart' && (
        <Box sx={{ mt: view === 'both' ? 1.5 : 0 }}>
          <ChartTable spec={spec} fmt={fmt} />
        </Box>
      )}
      {spec.source && (
        <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 0.5, textAlign: 'right' }}>
          {spec.source}
        </Typography>
      )}
    </Box>
  );
}

export default ChartCard;
